/**
 * SEMU Localization Form Controller
 * Lifecycle-safe country/language selection for the header.
 */

if (!customElements.get('semu-localization-form')) {
  customElements.define(
    'semu-localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.lifecycleActive = false;
        this.abortController = null;
        this.previousBodyOverflow = '';

        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleRootClick = this.handleRootClick.bind(this);
        this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
      }

      connectedCallback() {
        if (this.lifecycleActive) return;

        this.form = this.querySelector('form');
        this.dropdown = this.querySelector('[data-localization-dropdown]');
        this.countryInput = this.querySelector('input[name="country_code"]');
        this.languageInput = this.querySelector('input[name="locale_code"]');
        this.headerScope = this.closest('.section-header') || document;
        this.trigger = this.headerScope.querySelector('[data-localization-trigger]');

        if (!this.form || !this.dropdown || !this.trigger) return;

        this.lifecycleActive = true;
        this.abortController = new AbortController();
        const options = { signal: this.abortController.signal };

        this.trigger.addEventListener('click', this.handleTriggerClick, options);
        this.addEventListener('click', this.handleRootClick, options);
        document.addEventListener('keydown', this.handleDocumentKeydown, options);
        document.addEventListener('click', this.handleDocumentClick, options);
      }

      disconnectedCallback() {
        if (!this.lifecycleActive) return;

        this.close();
        this.abortController?.abort();
        this.abortController = null;
        this.lifecycleActive = false;
        this.form = null;
        this.dropdown = null;
        this.trigger = null;
      }

      handleTriggerClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.toggle();
      }

      handleRootClick(event) {
        if (event.target === this) {
          this.close();
          return;
        }

        const countryButton = event.target.closest('[data-country-code]');
        if (countryButton && this.contains(countryButton)) {
          event.preventDefault();
          const countryCode = countryButton.dataset.countryCode;
          if (this.countryInput && countryCode !== this.countryInput.value) {
            this.countryInput.value = countryCode;
            this.submitForm();
          }
          return;
        }

        const languageButton = event.target.closest('[data-language-code]');
        if (languageButton && this.contains(languageButton)) {
          event.preventDefault();
          const languageCode = languageButton.dataset.languageCode;
          if (this.languageInput && languageCode !== this.languageInput.value) {
            this.languageInput.value = languageCode;
            this.submitForm();
          }
        }
      }

      handleDocumentKeydown(event) {
        if (event.key === 'Escape' && this.classList.contains('is-active')) this.close();
      }

      handleDocumentClick(event) {
        if (
          this.classList.contains('is-active') &&
          !this.contains(event.target) &&
          event.target !== this.trigger &&
          !this.trigger?.contains(event.target)
        ) {
          this.close();
        }
      }

      toggle() {
        if (this.classList.contains('is-active')) {
          this.close();
        } else {
          this.open();
        }
      }

      open() {
        if (!this.dropdown || !this.trigger) return;

        this.closeOtherModals();
        this.previousBodyOverflow = document.body.style.overflow;
        this.classList.add('is-active');
        this.dropdown.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
      }

      close() {
        this.classList.remove('is-active');
        if (this.dropdown) this.dropdown.hidden = true;
        this.trigger?.setAttribute('aria-expanded', 'false');

        if (document.body.style.overflow === 'hidden') {
          document.body.style.overflow = this.previousBodyOverflow;
        }
      }

      closeOtherModals() {
        document.dispatchEvent(new CustomEvent('semu:search:close'));

        const mobileMenu = this.headerScope.querySelector?.('[data-mobile-menu].mobile-menu-drawer--active');
        mobileMenu?.close?.({ restoreFocus: false });
      }

      submitForm() {
        this.classList.add('is-submitting');
        this.form?.submit();
      }
    }
  );
}
